import {Injectable} from '@angular/core';
import {ApiAccessService, ApiAccessParameters} from './api-access.service';
import {NewPolicy, PropertySummary} from '../../models/new-policy';
import {Address} from '../../models/address';
import {OrderSummary} from '../../models/order-summary';
import {WorkOrderSummary} from '../../models/work-order-summary';
import {InspectionUploadArgs} from '../../models/inspection-upload';
import {NewEntity} from '../../models/new-entity';
import {UtilitiesService} from '../utilities.service';

@Injectable()
export class RealtorPortalService {
  private ogTenantId: string = "5EA8C224-B3FF-4F11-B09F-5EC0C65266EC";
  private lmTenantId: string = "";
  private sidebarInfo;
  constructor(private apiAccess: ApiAccessService) {}

  getCoverageLength() {
    return 1;
  }

  saveNewPolicy(newPolicy: NewPolicy) {
    // throw new Error( 'Not Implemented');
    return this.apiAccess.post(
        new ApiAccessParameters('WebQuote/convert', newPolicy));
  }

  authenticate(userName: string, password: string) {
    return this.apiAccess.get(new ApiAccessParameters(
        'WebUser/Authenticate', {userName: userName, password: password}));
  }

  authenticateUser(webUserArgs: WebUserCredentials) {
    return this.apiAccess.post(new ApiAccessParameters(
      'WebUser/AuthenticateUser',
      { UserName: webUserArgs.UserName, Password: webUserArgs.Password }));
  }

  getPromotion(code: string) {
    return this.apiAccess.get(
      new ApiAccessParameters('CouponCode', { code: code, tenantId: this.ogTenantId}));
  }

  getMarketStatesAndAreas() {
    return this.apiAccess.get(
      new ApiAccessParameters('MarketState/states-and-areas', { tenantId: this.ogTenantId}));
  }

  validateAddress(address: PropertySummary) {
    const addressObj = new Address();
    addressObj.Address1 = address.Address;
    addressObj.City = address.City;
    addressObj.Country = address.Country;
    addressObj.DwellingType = address.DwellingType;
    addressObj.SquareFootage = address.SquareFootage;
    addressObj.State = address.State;
    addressObj.YearBuilt = address.YearBuilt;
    addressObj.Zip = address.Zip;


    return this.apiAccess.post(
        new ApiAccessParameters('/address/validate', addressObj));
  }

  saveWebUser(webUser) {
    return this.apiAccess.post(new ApiAccessParameters('WebUser', webUser));
  }

  saveWebQuote(quote: NewPolicy) {
    return this.apiAccess.post(new ApiAccessParameters('WebQuote', quote));
  }



  getWebUserIdExists(userName: string) {
    return this.apiAccess.get(
        new ApiAccessParameters('WebUser/Exists', {userName: userName}));
  }


  getZipCodeInfo(zipCode: string) {
    return this.apiAccess.get(new ApiAccessParameters(
        'https://maps.googleapis.com/maps/api/geocode/json?address=' + zipCode +
        '&sensor=true&key=AIzaSyBqGIEvh3555HP37ZIc0qlfxI2EwDQibC4'));
  }


  getWebUserInfo(): Promise<any> {
    return this.apiAccess.get(new ApiAccessParameters('WebUser/GetUserInfo'));
  }

  getWebUserEntityRecord(): Promise<any> {
    return this.apiAccess.get(new ApiAccessParameters('WebUser/entity'));
  }

  unlinkEntities(): Promise<any> {
    return this.apiAccess.patch(
        new ApiAccessParameters('WebUser/unlink-entities'));
  }

  changePassword(oldPassword: string, password: string,
                 passwordConfirm: string) {
    return this.apiAccess.patch(
        new ApiAccessParameters('WebUser/ChangePassword', {
          OldPassword: oldPassword,
          NewPassword: password,
          NewPasswordConfirm: passwordConfirm
        }));
  }

  sendResetRequest(email: string) {
    return this.apiAccess.patch(
        new ApiAccessParameters('/WebUser/ResetPassword', {Email: email}));
  }

  getContractLanguage(salesItemCoverageId: string) {
    return this.apiAccess.get(
        new ApiAccessParameters('ContractLanguage/BySaleItemCoverageId',
                                {salesItemCoverageId: salesItemCoverageId}));
  }

  getQuotes() { return this.apiAccess.get(new ApiAccessParameters('Quotes')); }

  getOrders() {
    return this.apiAccess.get(new ApiAccessParameters(
        'RealEstateOrder', null, () => new OrderSummary()));
  }

  getMarketStates() {
    return this.apiAccess.get(new ApiAccessParameters('MarketState', { tenantId: this.ogTenantId} ));
  }

  getOrderDetail(orderId) {
    return this.apiAccess.get(
        new ApiAccessParameters('RealEstateOrder/' + orderId + '/Detail'));
  }

  getPolicySummaryById(id: string) {
    return this.apiAccess.get(new ApiAccessParameters('policy', {id: id}));
  }

  updateEstimatedClosingDate(orderId: string, estimatedClosingDate: Date) {
    return this.apiAccess.patch(new ApiAccessParameters(
        'RealEstateOrder/' + orderId + '/update-estimated-closing-date',
        {estimatedClosingDate: estimatedClosingDate}));
  }

  getClaimHistoryByPolicyId(policyId: string, endDate: Date = null, take = 5) {
    // ClaimHistory/by-policy-number
    if (!endDate) {
      endDate = new Date();
    }

    return this.apiAccess.get(
        new ApiAccessParameters('ClaimHistory/by-policy-id',
                                {id: policyId, endDate: endDate, take: take},
                                () => new WorkOrderSummary()));
  }

  uploadInspection(inspectionUpload: InspectionUploadArgs) {
    return this.apiAccess.post(
        new ApiAccessParameters('policy/upload-inspection', inspectionUpload));
  }

  getPlanSelection(address: PropertySummary, sellersCoverage: boolean) {
    return this.apiAccess.get(
        new ApiAccessParameters('PlanSelection/Realestate', {
          address: address.Address,
          city: address.City,
          state: address.State,
          zip: address.Zip,
          dwellingType: address.DwellingType,
          squareFootage: address.SquareFootage,
          sellersCoverage: sellersCoverage,
          yearBuilt: address.YearBuilt,
        tenantId: this.ogTenantId
        }));
  }

  findBranchByNameAndCity(name: string, city: string) {
    return this.apiAccess.get(new ApiAccessParameters(
        'RealEstateBranch/find/by-name-and-city', {name: name, city: city}));
  }

  findTitleCompanyByNameAndCity(name: string, city: string) {
    return this.apiAccess.get(new ApiAccessParameters(
        'TitleCompany/find/by-name-and-city', {name: name, city: city}));
  }

  findTitleCompanyByPhone(phoneNumber: string) {
    return this.apiAccess.get(new ApiAccessParameters(
        'TitleCompany/find/by-phone', {phoneNumber: phoneNumber}));
  }

  findTitleCompanyById(id: string) {
    return this.apiAccess.get(new ApiAccessParameters('TitleCompany/' + id));
  }

  getPrivateLabelDocuments() {
    return this.apiAccess.get(new ApiAccessParameters('PrivateLabelDocument'));
  }

  findRealEstateBranchById(id: string) {
    return this.apiAccess.get(
        new ApiAccessParameters('RealEstateBranch/' + id));
  }

  findBranchByPhone(phone: string) {
    return this.apiAccess.get(new ApiAccessParameters(
        'RealEstateBranch/find/by-phone', {phone: phone}));
  }

  findAgentByName(firstName: string, lastName: string, licenseNumber: string) {
    return this.apiAccess.get(
        new ApiAccessParameters('RealEstateAgent/find-by-name', {
          firstName: firstName,
          lastName: lastName,
          licenseNumber: licenseNumber,
        }));
  }


  findAgentByPhone(phoneNumber: string) {
    return this.apiAccess.get(
      new ApiAccessParameters('RealEstateAgent/find-by-phone', {
        phone: phoneNumber,
        tenantId: this.ogTenantId
      }));
  }

  findEscrowOfficerByName(firstName: string, lastName: string) {
    return this.apiAccess.get(
      new ApiAccessParameters('EscrowOfficer/find/by-name', {
          firstName: firstName,
          lastName: lastName,
        }));
  }
  findEscrowOfficerByPhone(phoneNumber: string) {
    return this.apiAccess.get(
      new ApiAccessParameters('EscrowOfficer/find-by-phone', {
        phoneNumber: phoneNumber,
        tenant: this.ogTenantId
      }));
  }
  findRealEstateTransactionCoordinatorByPhone(phoneNumber: string) {
    return this.apiAccess.get(new ApiAccessParameters('RealEstateCoordinator/find-by-phone', {
      phoneNumber: phoneNumber,
      tenantId: this.ogTenantId
    }));
  }

  findClosingOfficerCoordinatorByPhone(phoneNumber: string) {
    return this.apiAccess.get(
      new ApiAccessParameters('ClosingOfficerCoordinator/find-by-phone', { phoneNumber: phoneNumber, tenant: this.ogTenantId }));
  }
  getAgentsForBranch(branchId: string) {
    return this.apiAccess.get(
        new ApiAccessParameters('RealEstateBranch/' + branchId + '/agents'));
  }

  resendOrderConfirmation(webPolicyId: string, email: string) {
    return this.apiAccess.get(new ApiAccessParameters(
        'WebQuote/' + webPolicyId + '/resend-confirmation', {email: email}));
  }

  attachEntityToWebUser(entityId: string) {
    return this.apiAccess.patch(
        new ApiAccessParameters('WebUser/attach-entity/' + entityId));
  }

  saveNewBranch(branch: NewEntity) {
    if (!branch.Id) {
      branch.Id = UtilitiesService.newid();
    }

    return this.apiAccess.post(
        new ApiAccessParameters('RealEstateBranch', branch));
  }

  saveNewTitleCompany(titleCompany: NewEntity) {
    if (!titleCompany.Id) {
      titleCompany.Id = UtilitiesService.newid();
    }

    return this.apiAccess.post(
        new ApiAccessParameters('TitleCompany', titleCompany));
  }

  saveAgent(agent: NewEntity) {
    if (!agent.Id) {
      agent.Id = UtilitiesService.newid();
    }
    return this.apiAccess.post(
        new ApiAccessParameters('RealEstateAgent', agent));
  }

  saveCoordinator(coordinator: NewEntity) {
    if (!coordinator.Id) {
      coordinator.Id = UtilitiesService.newid();
    }

    return this.apiAccess.post(
        new ApiAccessParameters('RealEstateCoordinator', coordinator));
  }

  saveClosingOfficer(closingOfficer: NewEntity) {
    if (!closingOfficer.Id) {
      closingOfficer.Id = UtilitiesService.newid();
    }

    return this.apiAccess.post(
        new ApiAccessParameters('EscrowOfficer', closingOfficer));
  }

  saveClosingOfficerCoordinator(closingOfficerCoordinator: NewEntity) {
    if (!closingOfficerCoordinator.Id) {
      closingOfficerCoordinator.Id = UtilitiesService.newid();
    }

    return this.apiAccess.post(new ApiAccessParameters(
        'ClosingOfficerCoordinator', closingOfficerCoordinator));
  }

  getTotalSavingsByTrade() {
    return this.apiAccess.get(
        new ApiAccessParameters('real-estate/total-savings-by-trade'));
  }

  getWebUserExInfo() {
    return this.apiAccess.get(new ApiAccessParameters('WebUser/agent-info'));
  }

  saveWebUserExInfo(agent: any) {
    return this.apiAccess.post(
        new ApiAccessParameters('WebUser/agent-info', agent));
  }

  clearSideBarInfo() {
    this.sidebarInfo = null;
  }

  getSideBarInfo(clear = false): Promise<any> {
    if (clear) {
      this.sidebarInfo = null;
    }

    return new Promise((resolve, reject) => {
      if (this.sidebarInfo) {
        resolve(this.sidebarInfo);
      } else {
        this.apiAccess.get(new ApiAccessParameters(
                               'real-estate/get-sidebar-info'))
            .then(sideBarInfo => {
              this.sidebarInfo = sideBarInfo;
              resolve(this.sidebarInfo);
            });
      }
    });
  }

  getEscrowOfficersForTitleCompany(titleCompanyId: string) {
    return this.apiAccess.get(new ApiAccessParameters(
        'TitleCompany/' + titleCompanyId + '/escrow-officers'));
  }

  deleteQuote(quoteId: string) {
    return this.apiAccess.delete(
        new ApiAccessParameters('WebQuote/' + quoteId));
  }

  resendQuote(quoteId: string, sendTo: string) {
    return this.apiAccess.get(new ApiAccessParameters(
        'WebQuote/' + quoteId + '/resend', {email: sendTo}));
  }

  getWebQuote(quoteId: string) {
    return this.apiAccess.get(new ApiAccessParameters('WebQuote/' + quoteId));
  }

  getWarrantyLinkUrl() {
    return UtilitiesService.baseAPIUrl + 'RealEstateOrder/WarrantyLink';
  }

}

interface WebUserCredentials {
  UserName: string;
  Password: string;
}
